import { type FC } from 'react';

import { graphql, type ResultOf } from 'graphql-schema';
import { openMap } from '../../../utilities';
import { RentalModuleTypes } from '../../../constants';
import { RentalCard } from '../RentalCard';
import type { DiscoverRentalCardProps } from './types';

export const DISCOVER_RENTAL_VENUE_CARD_FRAGMENT = graphql(`
  fragment DiscoverRentalVenueCard on venues @_unmask {
    __typename
    _id
    place_id
    latitude
    image_url
    longitude
    shorthand_name
    formatted_address
    timeslots_summary {
      _id
      highest_overall_rental_price
      lowest_overall_rental_price
    }
    neighborhoodByNeighborhoodId {
      _id
      name
    }
    rentals(
      distinct_on: [sport]
      where: {
        is_discoverable: { _eq: true }
        timeslots: { start_time: { _gt: "now()" }, reservation: { _is_null: true } }
      }
    ) {
      _id
      sportBySport {
        _id
        name
      }
      images(limit: 1) {
        _id
        url
      }
    }
  }
`);

type Props = DiscoverRentalCardProps & {
  data: ResultOf<typeof DISCOVER_RENTAL_VENUE_CARD_FRAGMENT>;
};

/** Wrapped Rental component. Runs the graphql queries and populates the modules */

export const DiscoverRentalVenueCard: FC<Props> = ({
  data,
  onCardPress,
  overrideBannerMessage,
}) => {
  const {
    _id,
    rentals,
    place_id: placeId,
    image_url: venueImageUrl,
    shorthand_name: venueName,
    neighborhoodByNeighborhoodId,
    formatted_address: venueAddress,
    timeslots_summary,
  } = data;

  const neighborhoodName = neighborhoodByNeighborhoodId?.name || '';
  const imageUrl = venueImageUrl ?? rentals.find(rental => rental.images.at(0))?.images.at(0)?.url;
  const { lowest_overall_rental_price: lowPrice, highest_overall_rental_price: highPrice } =
    timeslots_summary ?? {};

  return (
    <RentalCard
      id={_id}
      imageUrl={imageUrl}
      onCardPress={onCardPress}
      bannerMessage={overrideBannerMessage}
      neighborhoodVenueModule={{
        type: RentalModuleTypes.NEIGHBORHOOD_VENUE,
        neighborhood: neighborhoodName,
        venue: {
          venueName,
          onPressVenue: () => openMap({ venueAddress, placeId }),
        },
      }}
      pricingModule={{
        type: RentalModuleTypes.RENTAL_PRICING,
        lowPrice,
        highPrice,
      }}
      displayNameModule={{ type: RentalModuleTypes.VENUE_DISPLAY_NAME, venueName }}
      detailModule={{
        type: RentalModuleTypes.RENTAL_SPORTS,
        availableSportTypes: rentals.map(sport => sport.sportBySport.name),
      }}
    />
  );
};

export default DiscoverRentalVenueCard;
